// Synonym groups for smarter asset search.
// Each group is a set of related terms — searching any term in a group
// will also match assets containing any other term in that group.

const synonymGroups: string[][] = [
  // Electronics – phones
  ["phone", "mobile", "smartphone", "iphone", "samsung", "galaxy", "pixel", "cell", "android"],
  // Electronics – laptops
  ["laptop", "macbook", "notebook", "chromebook", "thinkpad", "ultrabook"],
  // Electronics – tablets
  ["tablet", "ipad", "surface", "kindle", "e-reader"],
  // Electronics – TV & display
  ["tv", "television", "monitor", "display", "screen", "projector"],
  // Electronics – audio
  ["headphones", "earbuds", "airpods", "earphones", "headset"],
  ["speaker", "soundbar", "echo", "homepod", "sonos", "subwoofer"],
  // Electronics – cameras
  ["camera", "gopro", "webcam", "dslr", "mirrorless", "camcorder"],
  // Electronics – gaming
  ["gaming", "console", "playstation", "xbox", "nintendo", "switch", "controller", "gamepad"],
  // Electronics – computers
  ["computer", "pc", "desktop", "imac", "mac", "workstation"],
  // Electronics – networking
  ["router", "modem", "wifi", "mesh", "extender", "access point"],
  // Electronics – wearables
  ["watch", "smartwatch", "apple watch", "fitbit", "garmin", "wearable"],
  // Electronics – printing
  ["printer", "scanner", "copier", "all-in-one"],
  // Electronics – storage
  ["hard drive", "ssd", "external drive", "nas", "flash drive", "usb drive", "thumb drive"],
  // Electronics – drones
  ["drone", "quadcopter", "dji"],

  // Appliances – kitchen
  ["fridge", "refrigerator", "freezer"],
  ["oven", "stove", "range", "cooktop", "microwave"],
  ["dishwasher"],
  ["coffee", "coffee maker", "espresso", "nespresso", "keurig"],
  ["blender", "mixer", "food processor", "juicer"],
  ["toaster", "toaster oven", "air fryer"],
  // Appliances – laundry
  ["washer", "washing machine", "laundry"],
  ["dryer", "tumble dryer"],
  // Appliances – cleaning
  ["vacuum", "roomba", "dyson", "robot vacuum", "mop"],

  // Furniture
  ["sofa", "couch", "loveseat", "sectional", "futon"],
  ["desk", "standing desk", "writing desk", "workstation"],
  ["chair", "office chair", "recliner", "armchair"],
  ["bed", "mattress", "bed frame", "headboard"],
  ["table", "dining table", "coffee table", "end table", "side table"],
  ["shelf", "bookshelf", "shelving", "bookcase", "cabinet"],
  ["lamp", "light", "lighting", "chandelier", "floor lamp", "desk lamp"],
  ["rug", "carpet", "mat"],

  // Fitness
  ["bike", "bicycle", "cycling", "e-bike", "exercise bike"],
  ["treadmill", "elliptical", "rowing machine", "exercise"],
  ["weights", "dumbbell", "barbell", "kettlebell"],

  // Tools
  ["drill", "screwdriver", "power tool", "impact driver"],
  ["saw", "circular saw", "jigsaw", "table saw", "miter saw"],
  ["mower", "lawn mower", "trimmer", "leaf blower"],

  // Instruments
  ["guitar", "bass", "ukulele", "acoustic", "electric guitar"],
  ["piano", "keyboard", "synth", "synthesizer"],

  // Jewelry & accessories
  ["jewelry", "ring", "necklace", "bracelet", "earring"],
  ["bag", "backpack", "luggage", "suitcase", "briefcase"],
];

// Build a reverse lookup: term → Set of all related terms
const termIndex = new Map<string, Set<string>>();

for (const group of synonymGroups) {
  const lowerGroup = group.map((t) => t.toLowerCase());
  const termSet = new Set(lowerGroup);
  for (const term of lowerGroup) {
    const existing = termIndex.get(term);
    if (existing) {
      for (const t of termSet) existing.add(t);
    } else {
      termIndex.set(term, new Set(termSet));
    }
  }
}

/**
 * Expand a search query into an array of terms to search for.
 * Returns the original sanitized query plus any synonym matches.
 * Example: "phone" → ["phone", "mobile", "smartphone", "iphone", ...]
 */
export function expandSearchTerms(query: string): string[] {
  const sanitized = query.replace(/[^a-zA-Z0-9 ]/g, "").trim().toLowerCase();
  if (!sanitized) return [];

  const result = new Set<string>([sanitized]);

  // Check if query matches any synonym term (exact or as a substring of multi-word terms)
  for (const [term, synonyms] of termIndex) {
    if (term === sanitized || sanitized.includes(term) || term.includes(sanitized)) {
      for (const s of synonyms) {
        // For multi-word synonyms, use the individual words too
        result.add(s);
      }
    }
  }

  return Array.from(result);
}
